import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  FloatingOverlay,
  type Placement,
  type UseFloatingOptions,
} from '@floating-ui/react'
import { clsx } from 'clsx'
import {
  useEffect,
  useState,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
} from 'react'

import styles from './menu.module.css'

export interface MenuProps extends ComponentPropsWithoutRef<'div'> {
  /**
   * The element that triggers the menu
   */
  trigger: ReactNode

  /**
   * Callback when open state changes (controlled mode)
   */
  onOpenChange?: UseFloatingOptions['onOpenChange']

  /**
   * Placement of the menu relative to the trigger
   */
  placement?: Placement

  /**
   * Distance from the trigger element in pixels
   */
  offsetDistance?: number

  /**
   * Trigger type: 'click' or 'hover'
   */
  triggerType?: 'click' | 'hover'

  /**
   * Delay in milliseconds before showing the menu on hover (only applicable if triggerType is 'hover')
   */
  hoverDelay?: number
}

/**
 * Menu component using floating-ui for positioning and accessibility
 *
 * Supports both click and hover interactions with customizable positioning
 */
export function Menu(props: MenuProps): ReactElement {
  const {
    trigger,
    children,
    onOpenChange,
    placement = 'bottom-start',
    offsetDistance = 0,
    triggerType = 'click',
    hoverDelay = 0,
    className,
    ...rest
  } = props
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (onOpenChange) {
      onOpenChange(open)
    }
  }

  // On mobile we render the menu as a bottom sheet
  useEffect(() => {
    if (!isOpen) {
      return
    }

    // Check if we are on mobile
    const mediaQuery = window.matchMedia('(max-width: 640px)')

    // Update the state when the media query changes
    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)

    // Also check immediately to set the initial state
    setIsMobile(mediaQuery.matches)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [isOpen])

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement,
    middleware: [
      offset(offsetDistance),
      shift({ padding: 0 }),
      flip({ fallbackAxisSideDirection: 'end' }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context, { enabled: triggerType === 'click' })
  const hover = useHover(context, {
    enabled: triggerType === 'hover',
    delay: { open: hoverDelay },
    handleClose: safePolygon(),
  })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'menu' })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss, role])

  return (
    <>
      {/* Trigger Element */}
      <div ref={refs.setReference} {...getReferenceProps()}>
        {trigger}
      </div>

      {/* Menu Content */}
      {isOpen && (
        <FloatingPortal>
          <FloatingOverlay lockScroll={true} className={styles.overlay}></FloatingOverlay>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={isMobile ? { ...floatingStyles, top: 'auto' } : floatingStyles}
              className={clsx(styles.menu, className)}
              {...getFloatingProps(rest)}
            >
              {children}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}
